import React, { useState, useEffect } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Tag,
  Tooltip,
  Layout,
  message,
  Typography,
  Alert,
  Badge,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  DatabaseOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { getUserCriticalChanges } from "../api/userCriticalChanges/userCriticalChangesRequest";
import { getAgentsForFilter } from "../api/constants";
import { formatDateTime } from "../utils/dateUtils";

const { Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

function UserCriticalChangesPage() {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });
  const [filters, setFilters] = useState({
    changed_by: "",
    start_date: "",
    end_date: "",
    model: "",
    model_id: "",
  });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const initialFetch = async () => {
      await fetchChanges();
      await fetchUsers();
    };
    initialFetch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChanges = async (page = 1, pageSize = 25) => {
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        per_page: pageSize,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === "" || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getUserCriticalChanges(params);
      setChanges(response.data);
      setPagination({
        current: response.current_page,
        pageSize: response.per_page,
        total: response.total,
      });
    } catch (error) {
      message.error("Erreur lors du chargement des modifications critiques");
      console.error("Error fetching critical changes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await getAgentsForFilter();
      setUsers(data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleSearch = () => {
    fetchChanges(1, pagination.pageSize);
  };

  const handleTableChange = (newPagination) => {
    fetchChanges(newPagination.current, newPagination.pageSize);
  };

  const clearFilters = () => {
    setFilters({
      changed_by: "",
      start_date: "",
      end_date: "",
      model: "",
      model_id: "",
    });
  };

  const getModelDisplayName = (model) => {
    const modelMap = {
      "App\\Models\\Payment": "Paiement",
      "App\\Models\\Subscription": "Abonnement", 
      "App\\Models\\Echeance": "Échéance",
      "App\\Models\\Transaction": "Transaction",
      "App\\Models\\Agent": "Utilisateur",
      "App\\Models\\Client": "Client",
      "App\\Models\\Vehicle": "Véhicule",
      "App\\Models\\Infraction": "Infraction",
    };
    return modelMap[model] || model.replace("App\\Models\\", "");
  };

  const getFieldDisplayName = (field) => {
    const fieldMap = {
      amount: "Montant",
      registration_fee: "Frais d'inscription",
      payment_method: "Méthode de paiement",
      reference_number: "Numéro de référence",
      status: "Statut",
      full_name: "Nom complet",
      email: "Email",
      matricule: "Matriculation",
      price: "Prix",
    };
    return fieldMap[field] || field;
  };

  const getChangeTypeTag = (oldValue, newValue) => {
    if (oldValue === null || oldValue === "") {
      return <Tag color="green">Ajout</Tag>;
    } else if (newValue === null || newValue === "") {
      return <Tag color="red">Suppression</Tag>;
    } else {
      return <Tag color="orange">Modification</Tag>;
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return <Text type="secondary">null</Text>;
    }
    if (value === "") {
      return <Text type="secondary">vide</Text>;
    }
    return value;
  };

  const modelOptions = [
    { value: "Payment", label: "Paiements" },
    { value: "Subscription", label: "Abonnements" },
    { value: "Echeance", label: "Échéances" },
    { value: "Transaction", label: "Transactions" },
    { value: "User", label: "Utilisateurs" },
    { value: "Client", label: "Clients" },
    { value: "Vehicle", label: "Véhicules" },
    { value: "Infraction", label: "Infractions" },
  ];

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      sorter: (a, b) => b.id - a.id,
    },
    {
      title: "Type",
      key: "change_type",
      width: 100,
      render: (_, record) => getChangeTypeTag(record.old_value, record.new_value),
    },
    {
      title: "Modèle",
      dataIndex: "model",
      key: "model",
      width: 120,
      render: (model) => (
        <Tag color="blue" icon={<DatabaseOutlined />}>
          {getModelDisplayName(model)}
        </Tag>
      ),
    },
    {
      title: "ID Modèle",
      dataIndex: "model_id",
      key: "model_id",
      width: 90,
      render: (modelId) => <Text code>{modelId}</Text>,
    },
    {
      title: "Champ",
      dataIndex: "field",
      key: "field",
      width: 130,
      render: (field) => (
        <Tag color="geekblue">{getFieldDisplayName(field)}</Tag>
      ),
    },
    {
      title: "Ancienne valeur",
      dataIndex: "old_value",
      key: "old_value",
      width: 150,
      render: (value) => (
        <div style={{ wordBreak: "break-word" }}>
          {formatValue(value)}
        </div>
      ),
    },
    {
      title: "Nouvelle valeur",
      dataIndex: "new_value",
      key: "new_value",
      width: 150,
      render: (value) => (
        <div style={{ wordBreak: "break-word" }}>
          <Text strong>{formatValue(value)}</Text>
        </div>
      ),
    },
    {
      title: "Modifié par",
      dataIndex: "changed_by_name",
      key: "changed_by_name",
      width: 120,
      render: (name, record) => (
        <div>
          <UserOutlined className="mr-1" />
          <Text strong>{name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: "12px" }}>
            ID: {record.changed_by}
          </Text>
        </div>
      ),
    },
    {
      title: "Raison",
      dataIndex: "reason",
      key: "reason",
      width: 150,
      render: (reason) => {
        if (!reason) {
          return <Text type="secondary" italic>Aucune raison fournie</Text>;
        }
        return (
          <Tooltip title={reason}>
            <Text>{reason.length > 50 ? reason.substring(0, 50) + "..." : reason}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (date) => (
        <div>
          <CalendarOutlined className="mr-1" />
          <Text>{formatDateTime(date)}</Text>
        </div>
      ),
      sorter: (a, b) => new Date(b.created_at) - new Date(a.created_at),
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 120,
      render: (ip) => <Text code>{ip}</Text>,
    },
  ];

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Title level={2} className="mb-2">
            <HistoryOutlined className="mr-2" />
            Modifications Critiques des Utilisateurs
          </Title>
          <Text type="secondary">
            Journal d'audit des modifications sensibles effectuées dans le système
          </Text>
        </div>

        <Alert
          message="Journal d'Audit"
          description="Cette page affiche toutes les modifications critiques effectuées par les utilisateurs sur les données sensibles du système. Chaque changement est horodaté et lié à l'utilisateur responsable."
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
          className="mb-6"
        />

        {/* Filtres */}
        <Card className="mb-6 shadow-sm">
          <Title level={4} className="mb-4">Filtres de recherche</Title>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Modifié par"
                style={{ width: "100%" }}
                value={filters.changed_by}
                onChange={(value) => setFilters({ ...filters, changed_by: value })}
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {users.map((user) => (
                  <Option key={user.id_agent} value={user.id_agent}>
                    <UserOutlined className="mr-1" />
                    {user.full_name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Type de modèle"
                style={{ width: "100%" }}
                value={filters.model}
                onChange={(value) => setFilters({ ...filters, model: value })}
                allowClear
              >
                {modelOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    <DatabaseOutlined className="mr-1" />
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="ID du modèle"
                value={filters.model_id}
                onChange={(e) => setFilters({ ...filters, model_id: e.target.value })}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                style={{ width: "100%" }}
                onChange={(dates) => {
                  if (dates) {
                    setFilters({
                      ...filters,
                      start_date: dates[0]?.format("YYYY-MM-DD"),
                      end_date: dates[1]?.format("YYYY-MM-DD"),
                    });
                  } else {
                    setFilters({
                      ...filters,
                      start_date: "",
                      end_date: "",
                    });
                  }
                }}
                placeholder={["Date début", "Date fin"]}
              />
            </Col>
          </Row>
          <Row gutter={16} className="mt-4">
            <Col>
              <Space>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Rechercher
                </Button>
                <Button onClick={clearFilters}>
                  Effacer les filtres
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Statistiques rapides */}
        <Row gutter={16} className="mb-6">
          <Col xs={24} sm={8}>
            <Card>
              <div className="text-center">
                <Title level={3} className="mb-1">{pagination.total}</Title>
                <Text type="secondary">Total des modifications</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div className="text-center">
                <Title level={3} className="mb-1">
                  {new Set(changes.map(c => c.changed_by)).size}
                </Title>
                <Text type="secondary">Utilisateurs actifs</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <div className="text-center">
                <Title level={3} className="mb-1">
                  {new Set(changes.map(c => c.model)).size}
                </Title>
                <Text type="secondary">Types de modèles</Text>
              </div>
            </Card>
          </Col>
        </Row>

        {/* Tableau des résultats */}
        <Card className="shadow-sm">
          <div className="mb-4 flex justify-between items-center">
            <Title level={4} className="mb-0">
              Historique des modifications
              <Badge count={pagination.total} className="ml-2" />
            </Title>
          </div>
          
          <Table
            dataSource={changes}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} modifications`,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
            onChange={handleTableChange}
            scroll={{ x: 1400 }}
            size="small"
            bordered
          />
        </Card>
      </Content>
    </Layout>
  );
}

export default UserCriticalChangesPage;